import { RawMoneyComponent } from '@components/Shared/Money';
import { createFeature, Trans } from '@edreamsodigeo/frontend-core';
import { Body, Box, Pill } from '@edreamsodigeo/prisma-design-system';
import { useNumberOfPassengers } from 'clientState/providers/SearchRequestProvider';
import { Money } from 'generated/graphql';
import React, { FC } from 'react';
import { PRIME_DISCOUNT_VISIBILITY } from 'clientState/ab/aliases';

// 40% discount threshold
const DISCOUNT_THRESHOLD = 0.4;
const Highlight: FC = ({ children }) => {
  return (
    <Pill
      rounded
      type="promotional"
      sx={{
        marginLeft: 'tiny',
        marginRight: 'tiny',
        paddingLeft: '1x_small',
        paddingRight: '1x_small',
      }}
    >
      {children}
    </Pill>
  );
};

const PrimeDiscountCrownMoney = ({ currency, amount }) => (
  <RawMoneyComponent currency={currency}>{amount}</RawMoneyComponent>
);

export type PrimeDiscountCrownProps = {
  primePrice: Money;
  noPrimePrice: Money;
  onClick?: (e?) => void;
};

export const shouldDisplayPrimeCrown = (
  noPrimePrice: Money,
  primePrice: Money
) =>
  noPrimePrice.amount - noPrimePrice.amount * DISCOUNT_THRESHOLD >
  primePrice.amount;

export const PrimeDiscountCrown: FC<PrimeDiscountCrownProps> = ({
  primePrice,
  noPrimePrice,
  onClick,
  children,
}) => {
  const passengers = useNumberOfPassengers();
  const shouldDisplayCrown = shouldDisplayPrimeCrown(noPrimePrice, primePrice);
  const discountAmount =
    Math.round(noPrimePrice.amount - primePrice.amount) * passengers;

  return shouldDisplayCrown ? (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
      }}
      data-testid="prime-discount-crown"
    >
      <Box
        borderRadius="regular"
        border="2px solid"
        borderColor="primary.90"
        position="relative"
        sx={{
          zIndex: 999,
        }}
      >
        {children}
      </Box>
      <Box
        backgroundColor="primary.90"
        position="relative"
        paddingRight="small"
        paddingTop="1x_small"
        paddingBottom="2x_small"
        borderBottomRightRadius="regular"
        borderBottomLeftRadius="regular"
        sx={{
          margin: '-7px auto 0',
          textAlign: 'right',
        }}
      >
        <Body
          type="small"
          color="neutral.0"
          sx={{
            marginLeft: '2x_small',
            marginRight: '2x_small',
          }}
        >
          <Trans
            i18nKey="prime.pill.description"
            components={{
              highlight: <Highlight />,
              moneySaved: (
                <PrimeDiscountCrownMoney
                  currency={primePrice.currency}
                  amount={discountAmount}
                />
              ),
            }}
          />
        </Body>
      </Box>
    </Box>
  ) : (
    <>{children}</>
  );
};

const PrimeDiscountCrownNoShow: FC<PrimeDiscountCrownProps> = ({
  children,
}) => <>{children}</>;

const PrimeDiscountCrownComponent = createFeature({
  name: PRIME_DISCOUNT_VISIBILITY,
  variations: [
    PrimeDiscountCrownNoShow,
    PrimeDiscountCrownNoShow,
    PrimeDiscountCrown,
  ],
  version: 1,
});

export default PrimeDiscountCrownComponent;
